import type React from 'react';

import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import { Box, Button, CircularProgress, Container, Paper, Stack, Typography } from '@mui/material';

import logo from '@/assets/img/logo-knowme-with-text.svg';
import { themeColors } from '@/config/theme/themeColors.ts';
import {
  FallbackUIActionStyles,
  FallbackUICardStyles,
  FallbackUIContainerStyles,
  FallbackUIContentStyles,
  FallbackUIEyebrowStyles,
  FallbackUIFooterStyles,
  FallbackUIIconBadgeStyles,
  FallbackUIIllustrationLabelStyles,
  FallbackUIIllustrationStyles,
  FallbackUILogoImageStyles,
  FallbackUILogoStyles,
  FallbackUIMainStyles,
  FallbackUIMessageStyles,
  FallbackUIReportIconStyles,
  FallbackUIReportStyles,
  FallbackUITitleStyles,
} from '@/errorHandling/fallbackUI/fallbackUi.styles.ts';
import { reloadPage } from '@/utils/reloadPage.util.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';

export type ErrorReportStatus = 'failed' | 'pending' | 'sent';

type Props = {
  errorReportStatus: ErrorReportStatus;
};

export const FallbackUI: React.FC<Props> = ({ errorReportStatus }) => {
  const { t } = useTranslationWithPrefix('components.fallbackUi');
  const reportMessage = {
    failed: t('reportFailed'),
    pending: t('reportPending'),
    sent: t('reportSent'),
  }[errorReportStatus];
  const reportColor = errorReportStatus === 'failed' ? themeColors.red : themeColors.blue;
  const reportIcon =
    errorReportStatus === 'pending' ? (
      <CircularProgress aria-label={reportMessage} size={18} sx={{ color: reportColor }} />
    ) : errorReportStatus === 'sent' ? (
      <CheckCircleRoundedIcon />
    ) : (
      <ErrorOutlineRoundedIcon />
    );

  return (
    <Box component="main" role="alert" sx={FallbackUIMainStyles}>
      <Container maxWidth="lg" sx={FallbackUIContainerStyles}>
        <Box sx={FallbackUILogoStyles}>
          <Box alt="KnowMe" component="img" src={String(logo)} sx={FallbackUILogoImageStyles} />
        </Box>
        <Paper elevation={0} sx={FallbackUICardStyles}>
          <Box sx={FallbackUIIllustrationStyles}>
            <Box sx={FallbackUIIconBadgeStyles}>
              <ErrorRoundedIcon sx={{ color: themeColors.redPink, fontSize: { sm: 76, xs: 68 } }} />
            </Box>
            <Typography sx={FallbackUIIllustrationLabelStyles}>{t('eyebrow')}</Typography>
          </Box>

          <Stack spacing={3} sx={FallbackUIContentStyles}>
            <Box>
              <Typography sx={FallbackUIEyebrowStyles}>{t('eyebrow')}</Typography>
              <Typography component="h1" sx={FallbackUITitleStyles}>
                {t('title')}
              </Typography>
              <Typography sx={FallbackUIMessageStyles}>{t('message')}</Typography>
            </Box>

            <Stack
              aria-live="polite"
              direction="row"
              role="status"
              spacing={1.5}
              sx={{ ...FallbackUIReportStyles, borderColor: reportColor }}
            >
              <Box sx={{ ...FallbackUIReportIconStyles, color: reportColor }}>{reportIcon}</Box>
              <Typography>{reportMessage}</Typography>
            </Stack>

            <Button
              endIcon={<RefreshRoundedIcon />}
              onClick={reloadPage}
              sx={FallbackUIActionStyles}
              variant="contained"
            >
              {t('retry')}
            </Button>
          </Stack>
        </Paper>
        <Typography sx={FallbackUIFooterStyles}>{t('footer')}</Typography>
      </Container>
    </Box>
  );
};
